import React, { useState, useEffect } from 'react';
import { Select, Space, Typography, Alert, Spin, Tag, Tooltip } from 'antd';
import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/react-hooks';
import GET_ADVERTISER_BEACONS from '../../../../core/GraphQl/Queries/GET_ADVERTISER_BEACONS';

const { Text } = Typography;
const { Option } = Select;

const BeaconSelector = ({
  advertiserId,
  lineItemId,
  lineItemName,
  dspType,
  hasTransactionTags,
  transactionTags,
  value = [],
  onChange,
  disabled = false,
  hasActionBasedBidding = false,
  form,
  fieldName
}) => {
  const [selectedBeacons, setSelectedBeacons] = useState(value || []);
  const [showWarning, setShowWarning] = useState(false);

  // Only fetch beacons if conditions are met
  const shouldFetchBeacons = dspType === 'NEXXEN' && hasTransactionTags && advertiserId;

  const { data, loading, error } = useQuery(GET_ADVERTISER_BEACONS, {
    variables: {
      advertiserId,
      dspType,
      transactionTags: transactionTags || undefined,
      limit: 500 // Default limit from GraphQL
    },
    skip: !shouldFetchBeacons,
    fetchPolicy: 'cache-and-network'
  });

  useEffect(() => {
    // Show warning if action-based bidding is enabled but no beacons selected
    setShowWarning(hasActionBasedBidding && selectedBeacons.length === 0);
  }, [hasActionBasedBidding, selectedBeacons]);

  useEffect(() => {
    // Initialize with existing value
    if (value && value.length > 0) {
      setSelectedBeacons(value);
    }
  }, [value]);

  if (!shouldFetchBeacons) {
    return null;
  }

  const handleBeaconChange = (selectedBeaconIds) => {
    // Map selected IDs to beacon objects
    const selectedBeaconObjects = selectedBeaconIds.map(beaconId => {
      const beacon = data?.advertiserBeacons?.find(b => b.beaconId === beaconId);
      if (beacon) {
        return {
          beaconId: beacon.beaconId,
          name: beacon.name,
          advertiserId: beacon.advertiserId,
          beaconType: beacon.beaconType || deriveBeaconType(beacon)
        };
      }
      return null;
    }).filter(Boolean);

    setSelectedBeacons(selectedBeaconObjects);

    // Update form field if using Ant Design Form
    if (form && fieldName) {
      form.setFieldsValue({
        [fieldName]: selectedBeaconObjects
      });
    }

    // Call onChange callback
    if (onChange) {
      onChange(selectedBeaconObjects);
    }
  };

  const deriveBeaconType = (beacon) => {
    // Derive beacon type from name or placement
    const name = beacon.name?.toLowerCase() || '';
    const placement = beacon.beaconPlacement?.toLowerCase() || '';

    if (name.includes('transaction') || name.includes('tr') || placement.includes('checkout')) {
      return 'TRANSACTION';
    }
    if (name.includes('page') || name.includes('pv') || placement.includes('homepage')) {
      return 'PAGE_VIEW';
    }
    if (name.includes('cart') || placement.includes('cart')) {
      return 'ADD_TO_CART';
    }
    return 'CUSTOM';
  };

  const getBeaconTagColor = (beaconType) => {
    switch (beaconType) {
      case 'TRANSACTION': return 'green';
      case 'PAGE_VIEW': return 'blue';
      case 'ADD_TO_CART': return 'orange';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Text>Beacon Tracking</Text>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
      </Space>
    );
  }

  if (error) {
    return (
      <Alert
        message="Unable to load beacons"
        description="Failed to fetch available beacons. You can continue without beacon tracking."
        type="warning"
        showIcon
      />
    );
  }

  const availableBeacons = data?.advertiserBeacons || [];
  const approvedBeacons = availableBeacons.filter(
    beacon => beacon.workflowStatus === 'approved' && beacon.isValidated
  );

  if (approvedBeacons.length === 0) {
    return (
      <Alert
        message="No beacons available"
        description="No approved beacons found for this advertiser. Contact support to set up beacons."
        type="info"
        showIcon
      />
    );
  }

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Space>
        <Text strong>Beacon Tracking</Text>
        <Tooltip title="Select beacons to track conversions and user actions for this line item">
          <InfoCircleOutlined style={{ color: '#1890ff' }} />
        </Tooltip>
        {hasActionBasedBidding && (
          <Tag color="orange">Recommended</Tag>
        )}
      </Space>

      <Select
        mode="multiple"
        style={{ width: '100%' }}
        placeholder="Select beacons for conversion tracking"
        value={selectedBeacons.map(b => b.beaconId)}
        onChange={handleBeaconChange}
        disabled={disabled}
        allowClear
        showSearch
        filterOption={(input, option) => {
          // Get the beacon data from our list
          const beacon = approvedBeacons.find(b => b.beaconId === option.value);
          if (!beacon) return false;

          // Search in beacon name and placement
          const searchText = input.toLowerCase();
          return (
            beacon.name.toLowerCase().includes(searchText) ||
            (beacon.beaconPlacement && beacon.beaconPlacement.toLowerCase().includes(searchText)) ||
            (beacon.beaconType && beacon.beaconType.toLowerCase().includes(searchText))
          );
        }}
      >
        {approvedBeacons.map(beacon => {
          const beaconType = beacon.beaconType || deriveBeaconType(beacon);
          return (
            <Option key={beacon.beaconId} value={beacon.beaconId}>
              <Space>
                <span>{beacon.name}</span>
                <Tag color={getBeaconTagColor(beaconType)} style={{ marginLeft: 8 }}>
                  {beaconType}
                </Tag>
                {beacon.beaconPlacement && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    ({beacon.beaconPlacement})
                  </Text>
                )}
              </Space>
            </Option>
          );
        })}
      </Select>

      {showWarning && (
        <Alert
          message="Beacon tracking recommended"
          description={`Action-based bidding is enabled for "${lineItemName}". Beacon tracking is recommended for optimal performance.`}
          type="warning"
          showIcon
          closable
          onClose={() => setShowWarning(false)}
        />
      )}

      {selectedBeacons.length > 0 && (
        <Space wrap>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Selected: {selectedBeacons.length} beacon{selectedBeacons.length > 1 ? 's' : ''}
          </Text>
        </Space>
      )}
    </Space>
  );
};

export default BeaconSelector;